import { useEffect, useRef, type MutableRefObject } from 'react';
import { ApiError, NetworkError } from '../../lib/api/error.js';
import { playOriginals } from './api.js';
import { CONFIRMING_CODE, LIMIT_UNCHECKED_CODE, SEED_CHANGED_CODE } from './playError.js';
import type { PlayEnvelope, PlayRequest } from './types.js';

/**
 * Pause before each resend; the last one repeats until the deadline. Spaced out
 * because every resend of a round still being confirmed asks the wallet again,
 * and rounds are left being confirmed exactly when the wallet is overloaded.
 */
export const CONFIRM_RESEND_DELAYS_MS: readonly number[] = [2000, 5000, 10_000, 15_000];
/**
 * How long to keep asking. The server settles a drawn round by itself within
 * about a minute (the settle sweep), so past this the round's result reaches the
 * player through the balance and the feed instead.
 */
export const CONFIRM_DEADLINE_MS = 90_000;
/**
 * How long after the first send a refusal that names no round is still not
 * final. The first send may still be inside the game service when the client
 * gave up on it (the gateway stops waiting at 15 s, the service does not), so a
 * resend can find no round yet and be refused by a gate a moment before the
 * first send draws and books one. Past this the first send has either left a
 * round under the key, which the next resend is answered with, or none.
 */
export const REFUSAL_SETTLE_MS = 30_000;

/**
 * An answer that does not say whether the bet was placed: the round is being
 * confirmed, the request never got an answer, or something between here and the
 * game service failed. A result, or a refusal such as insufficient balance, an
 * unanswered limit check (GAME-018) or a seed that changed under the draw
 * (ORIG-023), is final.
 */
export function outcomeUnknown(err: unknown): boolean {
  if (err instanceof NetworkError) return true;
  if (!(err instanceof ApiError)) return false;
  if (err.code === CONFIRMING_CODE) return true;
  return err.status >= 500 && err.code !== LIMIT_UNCHECKED_CODE && err.code !== SEED_CHANGED_CODE;
}

/** A refusal the round under the key gave: the server answered it from that round. */
function namesRound(err: unknown): boolean {
  return err instanceof ApiError && typeof err.extensions?.roundId === 'string';
}

/** The session is gone; asking again cannot change the answer. */
function signedOut(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export interface PlayUntilKnownOptions {
  /** Stops the resends (the page went away); the last answer is thrown. */
  signal?: AbortSignal;
  /** Called once, on the first answer that does not say whether the bet was placed. */
  onConfirming?: () => void;
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Place one bet and learn its outcome. A send whose outcome is unknown is sent
 * again with the SAME body and idempotency key, which the server answers from
 * the round that key already has — the result, the refusal, or "still being
 * confirmed" — and never places twice. A fresh key per resend would be a second
 * bet, and giving up on the first unknown answer would leave the player told
 * nothing about a stake that may have been taken.
 */
export async function playUntilKnown(body: PlayRequest, opts: PlayUntilKnownOptions = {}): Promise<PlayEnvelope> {
  const wait = opts.wait ?? sleep;
  const now = opts.now ?? Date.now;
  const started = now();
  const deadline = started + CONFIRM_DEADLINE_MS;
  let resends = 0;
  for (;;) {
    try {
      return await playOriginals(body);
    } catch (err) {
      const settling = resends > 0 && !namesRound(err) && !signedOut(err) && now() < started + REFUSAL_SETTLE_MS;
      if (!outcomeUnknown(err) && !settling) throw err;
      if (resends === 0) opts.onConfirming?.();
      if (opts.signal?.aborted || now() >= deadline) throw err;
      await wait(CONFIRM_RESEND_DELAYS_MS[Math.min(resends, CONFIRM_RESEND_DELAYS_MS.length - 1)]!);
      if (opts.signal?.aborted) throw err;
      resends += 1;
    }
  }
}

/** A signal that aborts when the component unmounts, so its resends stop with it. */
export function useUnmountSignal(): MutableRefObject<AbortSignal | undefined> {
  const signal = useRef<AbortSignal | undefined>(undefined);
  useEffect(() => {
    const controller = new AbortController();
    signal.current = controller.signal;
    return () => controller.abort();
  }, []);
  return signal;
}
